
import { computed, defineComponent, ref } from "vue";
import { useToast } from "primevue/usetoast";

import { config } from "@/core/modules/config/objects/Config";
import { environment } from "@/core/modules/config/objects/environment";
import { eventBus } from "@/core/modules/eventBus/EventBus";
import { koruApp } from "@/main";
import { Navigation } from "@/core/modules/navigation/objects/Navigation";
import { navigationData } from "@/core/modules/navigation/objects/navigationData";
import { storeTypes } from "@/core/modules/store/types";
import { Theme } from "@/core/modules/theme/objects/Theme";
import { themeModel } from "@/core/modules/theme/models/ThemeModel";
import { useLocale } from "@/core/modules/locale/module";
import { User } from "@/core/modules/user/objects/User";
import { useStore } from "@/core/modules/store/module";

import BedrockHolder from "@/features/layout/BedrockHolder.vue";
import DevBar from "./DevBar.vue";
import Footer from "./Footer.vue";
import HistoryComponent from "@/core/modules/history/components/HistoryComponent.vue";
import Menu from "./Menu.vue";
import OfflineToggleComponent from "@/core/modules/offline/components/OfflineToggleComponent.vue";
import SearchComponent from "@/core/modules/search/components/SearchComponent.vue";
import PwaUpdateComponent from "@/core/modules/pwa/components/PwaUpdateComponent.vue";
import TopBar from "./TopBar.vue";

export default defineComponent({
  name: "Bedrock",
  components: {
    BedrockHolder,
    DevBar,
    Footer,
    HistoryComponent,
    Menu,
    OfflineToggleComponent,
    PwaUpdateComponent,
    SearchComponent,
    TopBar,
  },
  setup() {
    const { t } = useLocale();
    const store = useStore();
    const toast = useToast();

    const user: User = store.getters[storeTypes.getters.getUser];

    const getColorScheme = computed(() => {
      const selectedTheme: Theme = themeModel.getSelectedTheme();
      return selectedTheme.name;
    });

    const layoutMode = config.app.layout.mode;
    const menuTheme = config.app.layout.menuTheme;
    const menu = navigationData;
    const menuActive = ref(false);
    const overlayMenuActive = ref(false);
    const staticMenuDesktopInactive = ref(false);
    const staticMenuMobileActive = ref(false);
    const menuClick = ref(false);
    const searchActive = ref(false);
    const searchClick = ref(false);
    const userMenuClick = ref(false);
    const themeMenuClick = ref(false);
    const langMenuClick = ref(false);
    const topbarUserMenuActive = ref(false);
    const topbarThemeMenuActive = ref(false);
    const topbarLangMenuActive = ref(false);
    const notificationMenuClick = ref(false);
    const topbarNotificationMenuActive = ref(false);
    const rightMenuClick = ref(false);
    const rightMenuActive = ref(false);
    const configActive = ref(false);
    const configClick = ref(false);

    const containerClass = computed(() => {
      const selectedTheme: Theme = themeModel.getSelectedTheme();
      const colorScheme = selectedTheme.name;

      return [
        "layout-wrapper",
        {
          "layout-overlay": layoutMode === "overlay",
          "layout-static": layoutMode === "static",
          "layout-slim": layoutMode === "slim",
          "layout-sidebar-dim": colorScheme === "dim",
          "layout-sidebar-dark": colorScheme === "dark",
          "layout-overlay-active": overlayMenuActive.value,
          "layout-mobile-active": staticMenuMobileActive.value,
          "layout-static-inactive": staticMenuDesktopInactive.value && layoutMode === "static",
          "p-input-filled": koruApp.config.globalProperties.$appState.inputStyle === "filled",
          "p-ripple-disabled": koruApp.config.globalProperties.$primevue.config.ripple,
        },
        menuTheme,
      ];
    });

    return {
      containerClass,
      environment,
      config,
      store,
      storeTypes,
      menuActive,
      layoutMode,
      getColorScheme,
      menuTheme,
      Navigation,
      overlayMenuActive,
      staticMenuDesktopInactive,
      staticMenuMobileActive,
      menuClick,
      searchActive,
      searchClick,
      userMenuClick,
      themeMenuClick,
      langMenuClick,
      t,
      topbarUserMenuActive,
      topbarThemeMenuActive,
      topbarLangMenuActive,
      notificationMenuClick,
      topbarNotificationMenuActive,
      rightMenuClick,
      rightMenuActive,
      configActive,
      configClick,
      menu,
      toast,
      user,
    };
  },
  watch: {
    $route(): void {
      this.menuActive = false;
      this.toast.removeAllGroups();
    },
  },
  methods: {
    onDocumentClick(): void {
      if (!this.searchClick && this.searchActive) {
        this.onSearchHide();
      }
      if (!this.userMenuClick) {
        this.topbarUserMenuActive = false;
      }
      if (!this.themeMenuClick) {
        this.topbarThemeMenuActive = false;
      }
      if (!this.langMenuClick) {
        this.topbarLangMenuActive = false;
      }
      if (!this.notificationMenuClick) {
        this.topbarNotificationMenuActive = false;
      }
      if (!this.rightMenuClick) {
        this.rightMenuActive = false;
      }
      if (!this.menuClick) {
        if (this.isSlim()) {
          eventBus.emit("resetActiveIndex");
          this.menuActive = false;
        }
        if (this.overlayMenuActive || this.staticMenuMobileActive) {
          this.hideOverlayMenu();
        }
        this.unblockBodyScroll();
      }
      if (this.configActive && !this.configClick) {
        this.configActive = false;
      }
      this.searchClick = false;
      this.configClick = false;
      this.userMenuClick = false;
      this.themeMenuClick = false;
      this.langMenuClick = false;
      this.rightMenuClick = false;
      this.notificationMenuClick = false;
      this.menuClick = false;
    },
    onMenuClick(): void {
      this.menuClick = true;
    },
    onMenuButtonClick(event: Event): void {
      this.menuClick = true;
      this.topbarUserMenuActive = false;
      this.topbarThemeMenuActive = false;
      this.topbarLangMenuActive = false;
      this.topbarNotificationMenuActive = false;
      this.rightMenuActive = false;
      if (this.isOverlay()) {
        this.overlayMenuActive = !this.overlayMenuActive;
      }
      if (this.isDesktop()) {
        this.staticMenuDesktopInactive = !this.staticMenuDesktopInactive;
      } else {
        this.staticMenuMobileActive = !this.staticMenuMobileActive;
        if (this.staticMenuMobileActive) {
          this.blockBodyScroll();
        } else {
          this.unblockBodyScroll();
        }
      }
      event.preventDefault();
    },
    onMenuItemClick(event: unknown): void {
      const eventRecord: Record<string, Record<string, unknown>> = event as Record<string, Record<string, unknown>>;
      if (!eventRecord.item.items) {
        eventBus.emit("resetActiveIndex");
        this.hideOverlayMenu();
      }
      if (!eventRecord.item.items && this.isSlim()) {
        this.menuActive = false;
      }
    },
    onRootMenuItemClick(): void {
      this.menuActive = !this.menuActive;
    },
    onTopbarUserMenuButtonClick(event: Event): void {
      this.userMenuClick = true;
      this.topbarUserMenuActive = !this.topbarUserMenuActive;
      this.hideOverlayMenu();
      event.preventDefault();
    },
    onTopbarThemeMenuButtonClick(event: Event): void {
      this.themeMenuClick = true;
      this.topbarThemeMenuActive = !this.topbarThemeMenuActive;
      this.hideOverlayMenu();
      event.preventDefault();
    },
    onTopbarLangMenuButtonClick(event: Event): void {
      this.langMenuClick = true;
      this.topbarLangMenuActive = !this.topbarLangMenuActive;
      this.hideOverlayMenu();
      event.preventDefault();
    },
    onTopbarNotificationMenuButtonClick(event: Event): void {
      this.notificationMenuClick = true;
      this.topbarNotificationMenuActive = !this.topbarNotificationMenuActive;
      this.hideOverlayMenu();
      event.preventDefault();
    },
    toggleSearch(): void {
      this.searchActive = !this.searchActive;
      this.searchClick = true;
    },
    onSearchClick(): void {
      this.searchClick = true;
    },
    onSearchHide(): void {
      this.searchActive = false;
      this.searchClick = false;
    },
    onRightMenuClick() {
      this.rightMenuClick = true;
    },
    onRightMenuButtonClick(event: Event): void {
      this.rightMenuClick = true;
      this.rightMenuActive = !this.rightMenuActive;
      this.hideOverlayMenu();
      event.preventDefault();
    },
    onConfigClick(): void {
      this.configClick = true;
    },
    onConfigButtonClick(): void {
      this.configActive = !this.configActive;
      this.configClick = true;
    },
    hideOverlayMenu(): void {
      this.overlayMenuActive = false;
      this.staticMenuMobileActive = false;
      this.unblockBodyScroll();
    },
    blockBodyScroll(): void {
      if (document.body.classList) {
        document.body.classList.add("blocked-scroll");
      } else {
        document.body.className += " blocked-scroll";
      }
    },
    unblockBodyScroll(): void {
      if (document.body.classList) {
        document.body.classList.remove("blocked-scroll");
      } else {
        document.body.className = document.body.className.replace(
          new RegExp("(^|\\b)" + "blocked-scroll".split(" ").join("|") + "(\\b|$)", "gi"),
          " "
        );
      }
    },
    isSlim(): boolean {
      return this.layoutMode === "slim";
    },
    isOverlay(): boolean {
      return this.layoutMode === "overlay";
    },
    isDesktop(): boolean {
      return window.innerWidth > 991;
    },
  },
});
